import store from "@/store";
import { InspectKbn, SeibiKbn } from "@/model/InspectKbn";
import { InspectStaff } from "@/model/InspectStaff";
import { LiftUse } from "@/model/LiftUse";
import { InspectDetail } from "@/model/InspectDetail";
import { InspectionDetailState } from "@/store/modules/inspectionDetailStore";
import { IppanSeibiService } from "@/service/IppanSeibiService";

const namespace = "InspectionDetail";

export class InspectionShakenShuruiDetailStoreService {
  private ippanSeibiService: IppanSeibiService;
  private shakenShuruiPromise: Promise<void> | null = null;
  deSelSeibi: boolean = false;
  defaultSeibiMenu: string = "";

  // サービス初期化時に車検種類の選択管理枠をStoreへ登録する
  constructor() {
    this.ippanSeibiService = new IppanSeibiService();
    this.addNowSelected();
  }

  // InspectionDetail Store全体の現在状態を参照する
  get currentState(): InspectionDetailState {
    return store.getters[`${namespace}/currentState`];
  }

  // 車検種類が選択済みかどうかを参照する
  get nowSelected(): boolean {
    const nowSelected = store.getters[`${namespace}/getNowSelected`];
    if (nowSelected.shakenShurui === true || nowSelected.shakenShurui === false) {
      return nowSelected.shakenShurui;
    }
    this.addNowSelected();
    return false;
  }

  // 車検種類一覧の編集用データを参照する
  get shakenShurui(): SeibiKbn[] {
    return this.currentState.shakenShurui;
  }

  // 保存済みの整備メニューコードを参照する
  get seibiMenuCd(): string {
    return store.getters[`${namespace}/seibiMenuCd`];
  }

  // データ参照用に追加 2026/07/02
  // 車検種類として選択中の整備メニューコードを参照する
  get shakenShuruiSeibiMenuCd(): string {
    return this.currentState.shakenShuruiSeibiMenuCd;
  }

  // 車検種類名を参照する
  get shakenShuruiNm(): string {
    return store.getters[`${namespace}/getShakenShuruiNm`];
  }

  // 車検種類の更新日時を参照する
  get shakenShuruiSeibiMenuUpdatedAt(): string {
    return store.getters[`${namespace}/getShakenShuruiSeibiMenuUpdatedAt`];
  }

  // 車検種類一覧をStoreへ反映する
  setShakenShurui(payload: SeibiKbn[]) {
    return store.dispatch(`${namespace}/setShakenShurui`, payload);
  }

  // 車検種類の選択管理枠をStoreへ追加する
  addNowSelected() {
    return store.dispatch(`${namespace}/addNowSelected`, "shakenShurui");
  }

  // 車検種類の選択済み状態をStoreへ反映する
  setNowSelected(payload: boolean) {
    return store.dispatch(`${namespace}/setNowSelected`, {
      key: "shakenShurui",
      value: payload
    });
  }

  // 車検種類の選択状態からスタート可否判定用の状態を更新する
  checkDisableStart(arrData: any, isUnSelect?: boolean) {
    let checked: boolean = false;
    if (isUnSelect) {
      checked = true;
    } else if (arrData) {
      checked = arrData.some((data: any) => data.selected == "1");
    }
    this.setNowSelected(checked);
  }

  // 保存対象の整備メニューコードをStoreへ反映する
  setSeibiMenuCd(payload: string | number) {
    return store.dispatch(`${namespace}/setSeibiMenuCd`, payload);
  }

  // 車検種類名をStoreへ反映する
  setShakenShuruiNm(payload: string) {
    return store.dispatch(`${namespace}/setShakenShuruiNm`, payload);
  }

  // 車検種類の更新日時をStoreへ反映する
  setShakenShuruiSeibiMenuUpdatedAt(payload: string) {
    return store.dispatch(`${namespace}/setShakenShuruiSeibiMenuUpdatedAt`, payload);
  }

  // 共通処理から扱う車検種類一覧を返す
  get list(): SeibiKbn[] {
    return this.shakenShurui;
  }

  // 共通処理から車検種類一覧をStoreへ反映する
  setList(payload: SeibiKbn[]) {
    return this.setShakenShurui(payload);
  }

  // 共通処理から整備メニューコードをStoreへ反映する
  setCd(payload: string | number) {
    return this.setSeibiMenuCd(payload);
  }

  // テンプレート表示用の車検種類選択肢リストを生成する
  getTargetList() {
    return this.list
      .filter(data => data.delKbn != "9")
      .map((data: any) => {
        let iconStr: string = "";
        switch(data.value) {
          case '短時間車検':
            iconStr = 'icon-time';
            break;
          case '預かり車検':
            iconStr = 'icon-garage';
            break;
          case '再検査':
            iconStr = 'icon-engineer';
            break;
          case '立会車検':
            iconStr = 'icon-repair';
            break;
          default:
            break;
        }

        return {
          "mode": 1,
          "btnDel": data.delKbn == '-1',
          "btnSel": data.selected == '1',
          "btnFre": data.selected == '0',
          "btnUse": false,
          "key": data.seibiMenuCd,
          "dataSet": data.seibiMenuCd,
          "isDisabled": data.isDisabled == 1,
          "icon": iconStr,
          "label": data.seibiMenuNm,
          "labelEntends1": null,
          "labelEntends2": null,
          "labelEntends3": null,
        };
      });
  }

  // スタート時に選択中の車検種類を保存データへ反映する
  setStartInspectDetail(inspectionDetail: InspectDetail): boolean {
    const selectedData = this.list.find((data: SeibiKbn) => {
      return data.selected == "1";
    });
    if (!selectedData) { return false; }

    inspectionDetail.shakenShuruiSeibiMenuCd = String(selectedData.seibiMenuCd);
    inspectionDetail.shakenShuruiNm = String(selectedData.seibiMenuNm);
    inspectionDetail.isChangeShakenShuruiSeibiMenuCd = 0;
    if (this.shakenShuruiSeibiMenuCd) {
      if (this.shakenShuruiSeibiMenuCd != inspectionDetail.shakenShuruiSeibiMenuCd) {
        inspectionDetail.isChangeShakenShuruiSeibiMenuCd = 1;
      }
    }
    this.setCd(selectedData.seibiMenuCd);
    this.setShakenShuruiNm(selectedData.seibiMenuNm);
    inspectionDetail.shakenShuruiSeibiMenuUpdatedAt = this.shakenShuruiSeibiMenuUpdatedAt;
    return true;
  }

  // 取得済みの検査構成から車検種類の初期選択情報を反映する
  applyInitialInspectionDetail(data: any, inspectionDetail?: InspectDetail) {
    this.setCd(data.shakenShuruiSeibiMenuCd);
    this.setShakenShuruiNm(data.shakenShuruiNm);
    this.setShakenShuruiSeibiMenuUpdatedAt(data.shakenShuruiSeibiMenuUpdatedAt);
  }

  // 車検種類の選択クリックを処理する
  select(code: string) {
    this.defaultSeibiMenu = code;
    const shakenShurui = this.list;
    shakenShurui.map(data => {
      if (data.seibiMenuCd == code) {
        if (data.selected == "1") {
          data.selected = "0";
        } else {
          data.selected = "1";
        }
      } else {
        data.selected = "0";
      }
    });
    this.setList([...shakenShurui]);
    this.checkDisableStart(shakenShurui);
  }

  // 車検種類の「選択無し」状態を切り替える
  deSelect() {
    const shakenShurui = this.list;
    if (!this.deSelSeibi) {
      shakenShurui.map(data => (data.selected = "0"));
    }
    this.deSelSeibi = !this.deSelSeibi;
    this.setList([...shakenShurui]);
    this.checkDisableStart(shakenShurui, this.deSelSeibi);
    return this.deSelSeibi;
  }

  // 車検種類で「選択無し」を表示できるか返す
  canDeSelect() {
    return !this.list || this.list.length === 0;
  }

  // 車検種類が「選択無し」状態か返す
  isDeSelected() {
    return this.deSelSeibi;
  }

  // 共通呼び出し用の「選択無し」クリック処理
  clickDeSelect() {
    return this.deSelect();
  }

  // Storeに既存リストがある場合に選択状態を再評価する
  applyExistingList() {
    const shakenShurui = this.list;
    this.setList(shakenShurui);
    this.checkDisableStart(shakenShurui);
    return shakenShurui;
  }

  // 受付作業種別から初期選択候補の整備メニューコードを決める
  setDefaultSeibiMenuFromWorkShubetsu(res: any[], reservationState: any) {
    if (!res) { return; }
    const workShubetsuCd = reservationState ? reservationState.uketsuke.workShubetsuCd : "";
    const work = res.find((e: any) => e.workShubetsuCd == workShubetsuCd);
    if (work) {
      this.defaultSeibiMenu = work.seibiMenuCd;
    }
  }

  // API取得結果から車検種類一覧の初期選択状態を作成する
  getShakenShurui(
    loadSeibiMenuList: () => Promise<any>,
    inspectionDetail: InspectDetail,
    isUseIppanSeibi: boolean | null,
    sknSeibiShubetsuKbn: number | null
  ) {
    if (this.shakenShuruiPromise) { return this.shakenShuruiPromise; }

    this.shakenShuruiPromise = (async () => {
      try {
        const res = await loadSeibiMenuList();
        const shakenShuruiSeibiMenuCd = inspectionDetail.shakenShuruiSeibiMenuCd;
        const isSaved = shakenShuruiSeibiMenuCd != "";
        const isSeibifilter = (
          isUseIppanSeibi &&
          sknSeibiShubetsuKbn != null &&
          sknSeibiShubetsuKbn > -1
        );

        const seibiShubetsuList = res
          .filter((e: any) => {
            // 一般整備有効時のコースフィルタリング
            // 車検(=0)は車検、整備(>0)は整備で同じ整備種別の車検種類をリストアップする
            if (!isSeibifilter) { return true; }
            return sknSeibiShubetsuKbn == e.sknSeibiShubetsuKbn;
          })
          .map((data: any) => {
            const selected =
              (isSaved && shakenShuruiSeibiMenuCd == data.seibiMenuCd) ||
              (!isSaved && this.defaultSeibiMenu && this.defaultSeibiMenu == data.seibiMenuCd)
                ? "1"
                : "0";
            data.delKbn = (selected === "1" && data.delKbn == "9") ? "-1" : data.delKbn;
            if (selected === "1") { this.setNowSelected(true); }
            return { ...data, selected };
          });

        // 削除済みの車検種類を選択している場合のフォロー動作
        if (this.seibiMenuCd != "0") {
          const selectedData = seibiShubetsuList.filter((data: any) => data.selected === "1");
          if (!selectedData || selectedData.length == 0) {
            /**
             * 通常の選択ではここには来ない。
             * 過去の車検データを再閲覧時に選択した車検種類が削除済みの時の処理
             * 削除された車検種類のデータはないので表示用のデータを生成、追加する
             */
            const deletedNode: any = {
              delKbn: "-1",
              displayOrder: null,
              isShaken: null,
              seibiMenuCd: this.seibiMenuCd,
              seibiMenuNm: sknSeibiShubetsuKbn && sknSeibiShubetsuKbn > 0
                ? "削除済み整備種類"
                : "削除済み車検種類",
              selected: "1",
              sknSeibiShubetsuKbn: sknSeibiShubetsuKbn && sknSeibiShubetsuKbn > 0 ? 1 : 0
            };
            seibiShubetsuList.push(deletedNode);
            this.setNowSelected(true);
          }
        }
        this.setList(seibiShubetsuList);
      } catch (err) {
        this.setList([]);
      } finally {
        this.shakenShuruiPromise = null;
      }
    })();

    return this.shakenShuruiPromise;
  }

  // 整備メニューコード変更時に車検種類一覧の選択状態を同期する
  changeSeibiMenuCdSelection(
    isUserMode: boolean,
    sknSeibiShubetsuKbn: number | null
  ) {
    const shakenShurui = this.list;
    if (isUserMode) {
      shakenShurui.map(data => {
        if (data.seibiMenuCd == this.seibiMenuCd) {
          data.selected = "1";
        } else {
          data.selected = "0";
        }
        data.delKbn = (data.selected === "1" && data.delKbn == "9") ? "-1" : data.delKbn;
      });
      this.setList([...shakenShurui]);
      return false;
    }

    const seibiMenuCd = this.seibiMenuCd;
    const selectedData = shakenShurui.filter(data => data.seibiMenuCd == seibiMenuCd);
    if (selectedData && selectedData.length == 1 && selectedData[0].selected != "1") {
      this.select(seibiMenuCd);
      return true;
    }

    if ((selectedData.length === 0 || selectedData[0].selected != "1") && shakenShurui.length > 0) {
      /**
       * 通常の選択ではここには来ない。
       * 過去の車検データを再閲覧時に選択した車検種類が削除済みの時の処理
       * 削除された車検種類のデータはないので表示用のデータを生成、追加する
       */
      // ここでは項目の追加だけ行う
      const deletedNode: any = {
        delKbn: "-1",
        displayOrder: null,
        isShaken: null,
        seibiMenuCd: this.seibiMenuCd,
        seibiMenuNm: sknSeibiShubetsuKbn && sknSeibiShubetsuKbn > 0
          ? "削除済み整備種類"
          : "削除済み車検種類",
        selected: "0",
        sknSeibiShubetsuKbn: sknSeibiShubetsuKbn && sknSeibiShubetsuKbn > 0 ? 1 : 0
      };
      this.setList([...shakenShurui, deletedNode]);
      this.select(this.seibiMenuCd);
      return true;
    }

    return false;
  }

  // 車検種類一覧を既存StoreまたはAPIから取得する
  getDataList(loadInitialList: () => Promise<any>) {
    if (this.list.length > 0) {
      this.applyExistingList();
      return {
        hasExistingList: true,
        promise: null
      };
    }
    return {
      hasExistingList: false,
      promise: loadInitialList()
    };
  }
}
